import type { HTMLAttributes, ReactNode } from 'react';
import { Children, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconArrowLeftHeaderCourses,
  iconStudentInProgress,
  iconStudentStart,
  iconStudentWatch,
} from '../../assets';
import { useTheme } from '../../theme';

/** Qual ícone 20×20 exibir antes do título da seção. */
export type StudentCourseSectionTitleKind =
  | 'in_progress'
  | 'available_for_you'
  | 'watch_again';

const TITLE_KIND_ICON_SRC: Record<StudentCourseSectionTitleKind, string> = {
  in_progress: iconStudentInProgress,
  available_for_you: iconStudentStart,
  watch_again: iconStudentWatch,
};

export interface StudentCourseCarouselSectionProps extends Omit<
  HTMLAttributes<HTMLElement>,
  'title' | 'children'
> {
  title: ReactNode;
  countLabel: ReactNode;
  children: ReactNode;
  /** Ícone à esquerda do título (20×20), conforme o status da seção. */
  titleKind?: StudentCourseSectionTitleKind;
}

export function StudentCourseCarouselSection({
  title,
  countLabel,
  children,
  titleKind,
  style,
  ...rest
}: StudentCourseCarouselSectionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const cc = theme.palette.studentDashboard.courseCarousel;
  const wa = cc.watchAgainCountBadge;
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollOverflow, setScrollOverflow] = useState(false);
  const childCount = Children.count(children);

  const updateScrollButtons = useCallback(() => {
    const el = carouselRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setScrollOverflow(false);
      return;
    }

    // `scrollLeft` pode ficar levemente negativo em alguns browsers.
    const left = Math.max(0, el.scrollLeft);
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const overflow = maxLeft > 1;

    setScrollOverflow(overflow);
    setCanScrollLeft(overflow && left > 0);
    setCanScrollRight(overflow && left < maxLeft - 1);
  }, []);

  useLayoutEffect(() => {
    updateScrollButtons();
  }, [updateScrollButtons, childCount]);

  useEffect(() => {
    updateScrollButtons();

    const el = carouselRef.current;
    if (!el) return;

    const handleScroll = () => updateScrollButtons();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            updateScrollButtons();
          })
        : null;
    ro?.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      ro?.disconnect();
    };
  }, [updateScrollButtons, childCount]);

  const getScrollDelta = useCallback((): number => {
    const el = carouselRef.current;
    if (!el) return 0;

    const firstChild = el.children[0] as HTMLElement | undefined;
    if (!firstChild) return el.clientWidth;

    const childWidth = firstChild.getBoundingClientRect().width;
    const gap = Number.parseFloat(getComputedStyle(el).gap || '0') || 0;

    const delta = childWidth + gap;
    return delta > 0 ? delta : el.clientWidth;
  }, []);

  const handlePrev = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    if (!canScrollLeft) return;

    const delta = getScrollDelta();
    if (delta <= 0) return;

    el.scrollBy({ left: -delta, behavior: 'smooth' });
  }, [canScrollLeft, getScrollDelta]);

  const handleNext = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    if (!canScrollRight) return;

    const delta = getScrollDelta();
    if (delta <= 0) return;

    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, [canScrollRight, getScrollDelta]);

  const sectionTitleStyle = {
    margin: 0,
    color: theme.palette.text.primary,
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    lineHeight: theme.typography.lineHeight.tight,
  };

  const sectionCountStyle =
    titleKind === 'watch_again'
      ? {
          margin: 0,
          borderRadius: theme.shape.borderRadius.full,
          border: wa.border,
          color: wa.text,
          backgroundColor: wa.background,
          fontSize: theme.typography.fontSize.xs,
          fontWeight: theme.typography.fontWeight.medium,
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          textTransform: 'lowercase' as const,
        }
      : {
          margin: 0,
          borderRadius: theme.shape.borderRadius.full,
          border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
          color: cc.countBadgeText,
          backgroundColor: cc.countBadgeBackground,
          fontSize: theme.typography.fontSize.xs,
          fontWeight: theme.typography.fontWeight.medium,
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          textTransform: 'lowercase' as const,
        };

  const carouselStyle = {
    display: 'flex',
    gap: theme.spacing.lg,
    // Mantemos `scrollLeft` para navegar, mas removemos o scroll manual.
    overflowX: 'hidden' as const,
    overflowY: 'hidden' as const,
    scrollSnapType: 'x mandatory' as const,
    scrollBehavior: 'smooth' as const,
    WebkitOverflowScrolling: 'touch' as const,
    touchAction: 'pan-y' as const,
    paddingBottom: theme.spacing.sm,
  };

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.lg,
        ...style,
      }}
      {...rest}
    >
      <style>
        {`
          .student-course-carousel-arrow {
            transition: opacity ${cc.carouselArrowTransitionMs}ms ease, transform ${cc.carouselArrowTransitionMs}ms ease;
            opacity: ${cc.carouselArrowOpacityDefault};
          }
          .student-course-carousel-arrow:hover:enabled {
            opacity: ${cc.carouselArrowOpacityHover};
            transform: translateY(${cc.carouselArrowHoverTranslateY}px);
          }
          .student-course-carousel-arrow:disabled {
            opacity: ${cc.carouselArrowOpacityDisabled};
            cursor: not-allowed;
          }
        `}
      </style>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            minWidth: 0,
          }}
        >
          {titleKind ? (
            <img
              src={TITLE_KIND_ICON_SRC[titleKind]}
              alt=""
              width={cc.sectionTitleIconSize}
              height={cc.sectionTitleIconSize}
              style={{ display: 'block', flexShrink: 0 }}
            />
          ) : null}
          <h2 style={{ ...sectionTitleStyle, minWidth: 0 }}>{title}</h2>
        </div>
        {scrollOverflow ? <p style={sectionCountStyle}>{countLabel}</p> : null}
      </div>

      <div style={{ position: 'relative' }}>
        {scrollOverflow ? (
          <button
            type="button"
            aria-label={t('student_dashboard.feed.carousel_prev_aria')}
            disabled={!canScrollLeft}
            onClick={handlePrev}
            className="student-course-carousel-arrow"
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              width: cc.navButtonSize,
              height: cc.navButtonSize,
              borderRadius: theme.shape.borderRadius.full,
              border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
              // Overlay com leve transparência pra não “sumir” o espaçamento visual.
              backgroundColor: cc.arrowButtonOverlayBackground,
              backdropFilter: 'blur(2px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <img
              src={iconArrowLeftHeaderCourses}
              alt=""
              width={cc.navIconSize}
              height={cc.navIconSize}
            />
          </button>
        ) : null}

        <div
          ref={carouselRef}
          style={{
            ...carouselStyle,
            flex: 1,
            minWidth: 0,
          }}
        >
          {children}
        </div>

        {scrollOverflow ? (
          <button
            type="button"
            aria-label={t('student_dashboard.feed.carousel_next_aria')}
            disabled={!canScrollRight}
            onClick={handleNext}
            className="student-course-carousel-arrow"
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translate(50%, -50%)',
              zIndex: 2,
              width: cc.navButtonSize,
              height: cc.navButtonSize,
              borderRadius: theme.shape.borderRadius.full,
              border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
              backgroundColor: cc.arrowButtonOverlayBackground,
              backdropFilter: 'blur(2px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <img
              src={iconArrowLeftHeaderCourses}
              alt=""
              width={cc.navIconSize}
              height={cc.navIconSize}
              style={{ transform: 'scaleX(-1)' }}
            />
          </button>
        ) : null}
      </div>
    </section>
  );
}
